import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";

export default function TeamPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Team · Admin";
  }, []);

  const { list, remove } = useResource("team", { toastLabel: "team member" });
  const { data, isLoading } = list({
    page,
    limit: 20,
    sort: "sortOrder:asc",
    ...(search && { search }),
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">About</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Team Members</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} member{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/team/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New member</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search members by name…"
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/team/${row._id}`)}
          columns={[
            {
              key: "name",
              label: "Member",
              render: (r) => (
                <div className="flex items-center gap-3">
                  <ImageThumbnail src={r.photo} alt={r.name} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">{r.name}</p>
                    <p className="truncate text-xs text-text-muted">{r.role || "—"}</p>
                  </div>
                </div>
              ),
            },
            {
              key: "bio",
              label: "Bio",
              render: (r) => (
                <span className="text-text-secondary">
                  {r.bio ? r.bio.slice(0, 100) + (r.bio.length > 100 ? "…" : "") : "—"}
                </span>
              ),
            },
            { key: "sortOrder", label: "Sort", render: (r) => r.sortOrder ?? 0 },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/team/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No team members yet",
            description: "Add your first team member to populate the public Our Team page.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.name}"?`}
        description="The member will be removed from the public Our Team page."
        confirmLabel="Delete member"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
